import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Eye, EyeOff, Loader2, MessageCircleQuestion, MailCheck, TimerReset, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function Login() {
    const { login, resendVerification } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [needsVerify, setNeedsVerify] = useState(false);
    const [resending, setResending] = useState(false);
    // Rate-limit state — countdown auto-tick, auto-clears when time is up.
    const [lockedUntil, setLockedUntil] = useState(0);   // epoch ms; 0 = not locked
    const [nowTick, setNowTick] = useState(Date.now());
    useEffect(() => {
        if (!lockedUntil) return;
        const id = setInterval(() => setNowTick(Date.now()), 1000);
        return () => clearInterval(id);
    }, [lockedUntil]);
    const isLocked = lockedUntil > nowTick;
    const secondsLeft = Math.max(0, Math.ceil((lockedUntil - nowTick) / 1000));
    useEffect(() => {
        if (lockedUntil && !isLocked) {
            // Countdown just finished — clear alert automatically.
            setLockedUntil(0);
            setError("");
        }
    }, [isLocked, lockedUntil]);
    const countdownLabel = useMemo(() => {
        const m = Math.floor(secondsLeft / 60);
        const s = secondsLeft % 60;
        return `${m} min${m === 1 ? "" : "uti"} ${String(s).padStart(2, "0")} sec`;
    }, [secondsLeft]);

    const submit = async (e) => {
        e.preventDefault();
        if (isLocked) return;
        setError("");
        setNeedsVerify(false);
        setLoading(true);
        try {
            const u = await login(email, password);
            toast.success(`Bentornato, ${u.full_name.split(" ")[0]}!`);
            const redirect = params.get("redirect");
            if (redirect) navigate(redirect);
            else if (u.role === "admin") navigate("/admin");
            else navigate("/dashboard");
        } catch (err) {
            const msg = apiError(err);
            // 1) Rate limit — surfaced by backend as 429 with detail "RATE_LIMITED: …"
            //    Retry-After (seconds) is exposed on `X-Retry-After-Seconds`.
            if (msg.includes("RATE_LIMITED") || err?.response?.status === 429) {
                const secs =
                    Number(err?.response?.headers?.["x-retry-after-seconds"]) ||
                    Number(err?.response?.headers?.["retry-after"]) ||
                    15 * 60;
                setLockedUntil(Date.now() + secs * 1000);
                setNowTick(Date.now());
                setError("");
                setNeedsVerify(false);
                return;
            }
            if (msg.includes("EMAIL_NOT_VERIFIED")) {
                setNeedsVerify(true);
                setError("Devi verificare l'email prima di accedere.");
            } else {
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResending(true);
        try {
            const data = await resendVerification(email);
            if (data?.verification_url) {
                navigate("/verifica-email", {
                    state: {
                        email,
                        email_sent: false,
                        verification_url: data.verification_url,
                    },
                });
            } else {
                toast.success("Se l'account esiste, ti abbiamo inviato una nuova email.");
            }
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-start justify-center bg-slate-50 px-4 py-14 sm:py-20">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center hover:opacity-80">
                        <img src="/brand-header.png" alt="CondominioNow" className="h-[60px] w-auto object-contain" />
                    </Link>
                </div>
                <Card className="border-slate-200 shadow-sm">
                    <CardHeader className="space-y-1 pb-6">
                        <h1 className="font-serif text-3xl font-semibold">Bentornato</h1>
                        <p className="text-sm text-muted-foreground">
                            Accedi per gestire le tue domande.
                        </p>
                    </CardHeader>
                    <CardContent>
                        <form
                            onSubmit={submit}
                            className="space-y-4"
                            data-testid="login-form"
                            noValidate
                        >
                            {error && !needsVerify && !isLocked && (
                                <Alert variant="destructive" data-testid="login-error">
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}
                            {isLocked && (
                                <Alert
                                    data-testid="login-rate-limited"
                                    className="border-amber-300 bg-amber-50 text-amber-900"
                                >
                                    <TimerReset className="size-4" />
                                    <AlertDescription>
                                        <div className="font-semibold">Troppi tentativi di accesso</div>
                                        <div className="text-sm mt-1">
                                            Per motivi di sicurezza il login è stato temporaneamente
                                            bloccato. Riprova tra{" "}
                                            <span
                                                className="font-mono font-bold text-amber-950"
                                                data-testid="login-rate-limit-countdown"
                                            >
                                                {countdownLabel}
                                            </span>{" "}
                                            oppure utilizza il recupero password.
                                        </div>
                                        <Button
                                            asChild
                                            variant="outline"
                                            size="sm"
                                            className="mt-3 bg-white"
                                            data-testid="login-rate-limited-recover"
                                        >
                                            <Link to="/recupera-password">
                                                <KeyRound className="size-3.5 mr-1.5" />
                                                Recupera password
                                            </Link>
                                        </Button>
                                    </AlertDescription>
                                </Alert>
                            )}
                            {needsVerify && (
                                <Alert
                                    data-testid="login-needs-verify"
                                    className="border-amber-300 bg-amber-50 text-amber-900"
                                >
                                    <MailCheck className="size-4" />
                                    <AlertDescription>
                                        <div className="font-semibold">
                                            La tua email non è ancora stata verificata.
                                        </div>
                                        <div className="text-sm mt-1">
                                            Controlla la casella (anche Spam) o richiedi un
                                            nuovo link di verifica.
                                        </div>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="mt-3 bg-white"
                                            onClick={handleResend}
                                            disabled={resending || !email}
                                            data-testid="login-resend-verification"
                                        >
                                            {resending && (
                                                <Loader2 className="size-3 mr-1.5 animate-spin" />
                                            )}
                                            Reinvia email di verifica
                                        </Button>
                                    </AlertDescription>
                                </Alert>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="login-email"
                                    placeholder="tuo@email.it"
                                    autoFocus
                                />
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="password">Password</Label>
                                    <Link
                                        to="/recupera-password"
                                        className="text-xs text-primary font-medium hover:underline"
                                        data-testid="login-forgot-password"
                                    >
                                        Password dimenticata?
                                    </Link>
                                </div>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPwd ? "text" : "password"}
                                        required
                                        autoComplete="current-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        data-testid="login-password"
                                        placeholder="••••••••"
                                        className="pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPwd(!showPwd)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        aria-label={showPwd ? "Nascondi password" : "Mostra password"}
                                        data-testid="login-toggle-password"
                                    >
                                        {showPwd ? (
                                            <EyeOff className="size-4" />
                                        ) : (
                                            <Eye className="size-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            <Button
                                data-testid="login-submit"
                                type="submit"
                                className="w-full h-11"
                                disabled={loading || isLocked}
                            >
                                {loading && <Loader2 className="size-4 mr-2 animate-spin" />}
                                {isLocked
                                    ? `Riprova tra ${countdownLabel}`
                                    : loading
                                        ? "Accesso in corso…"
                                        : "Accedi"}
                            </Button>
                            <div className="text-center text-sm text-muted-foreground pt-2">
                                Non hai un account?{" "}
                                <Link
                                    to="/registrati"
                                    className="text-primary font-semibold hover:underline"
                                    data-testid="login-to-register"
                                >
                                    Registrati
                                </Link>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
